import { useMemo, useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import { formatDistanceMeters, getZeroDistanceLabel } from '../../utils/distanceUtils';

const WaypointCorrectionDialog = ({
  isOpen,
  onClose,
  waypointName,
  coLocatedWaypoints = [],
  onConfirm,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const { distanceUnit } = useDateTimePreferences();
  const [selectedOption, setSelectedOption] = useState('none');

  useEffect(() => {
    if (!isOpen) {
      setSelectedOption('none');
    }
  }, [isOpen]);

  const options = useMemo(() => {
    const waypointOptions = (coLocatedWaypoints || []).map((wp) => ({
      value: String(wp.waypoint_id),
      label: wp.display_name || wp.waypoint_name,
      meta: wp.distance_meters === null || typeof wp.distance_meters === 'undefined'
        ? null
        : formatDistanceMeters(wp.distance_meters, distanceUnit, t, {
          compact: false,
          zeroLabel: getZeroDistanceLabel(distanceUnit, t)
        })
    }));

    return [
      ...waypointOptions,
      { value: 'none', label: t('justResetWaypoint'), meta: null }
    ];
  }, [coLocatedWaypoints, distanceUnit, t]);

  const handleConfirm = () => {
    if (selectedOption === 'none') {
      onConfirm?.(null);
      return;
    }
    const parsed = parseInt(selectedOption, 10);
    onConfirm?.(Number.isFinite(parsed) ? parsed : null);
  };

  return (
    <ConfirmDialogShell
      isOpen={isOpen}
      onClose={onClose}
      title={t('confirmCorrection')}
      onConfirm={handleConfirm}
      confirmLabel={t('confirmCorrection')}
      confirmVariant="danger"
      iconVariant="danger"
      showIcon={false}
      isLoading={isLoading}
      size="md"
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {t('whichWaypointAreYouAt')}
        </p>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {waypointName}
        </p>

        {coLocatedWaypoints.length > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('coLocatedWaypointsFound')}
          </p>
        )}

        <div className="space-y-2">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-3 rounded border border-gray-200 dark:border-gray-700 p-2 cursor-pointer"
            >
              <input
                type="radio"
                name="waypoint-correction-option"
                value={option.value}
                checked={selectedOption === option.value}
                onChange={(e) => setSelectedOption(e.target.value)}
                disabled={isLoading}
              />
              <div className="flex-1 text-sm text-gray-800 dark:text-gray-200">
                {option.label}
              </div>
              {option.meta && (
                <div className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {option.meta}
                </div>
              )}
            </label>
          ))}
        </div>
      </div>
    </ConfirmDialogShell>
  );
};

export default WaypointCorrectionDialog;
